import { createErrorHandler } from "@/app-utils/errors.ts";
import { savedItemsListingExists, updateSavedItemsData, updateSavedItemsListingData } from "@/app-utils/immutable.ts";
import { type ISelectionModel, emptySelectionModel } from "@/app-utils/selection-model.ts";
import type { Dictionary } from "@/app-utils/type.ts";
import type {
  Dashboard,
  InputDashboardInput,
  Project,
  ProjectList,
  RecentItem,
  SampleCount,
  SavedItem,
  SavedItemsItemsArgs,
  SavedItemsListing,
} from "@/graphql.ts";
import { selMessage } from "@/messages/message.ts";
import { CardIcons } from "@/settings/assets-settings.ts";
import { LICENSE_MODULE, NOT_FOUND_EXCEPTION, SORT_OPTIONS } from "@/settings/settings.ts";
import type WebApiClient from "../../api/WebApiClient.js";
import { notify } from "../../modules/notifications/notify.js";
import type { IGenericDashboardCard } from "../../ui-modules/dashboard/AddDashboardCardPanel.js";
import type { IDashboardConfig } from "../../ui-modules/dashboard/DashboardView.js";
import type { IProjectsListCardConfig } from "../../ui-modules/dashboard/ProjectsDashboardCard.js";
import type { IRecentActivitiesCardConfig } from "../../ui-modules/dashboard/RecentActivitiesDashboardCard.js";
import type { ISampleStatusesCardConfig } from "../../ui-modules/dashboard/SampleStatusDashboardCard.js";
import type { ISavedItemsCardConfig } from "../../ui-modules/dashboard/SavedItemsDashboardCard.js";
import {
  LOAD_CARD_PROJECTS_FAILURE,
  LOAD_CARD_PROJECTS_REQUEST,
  LOAD_CARD_PROJECTS_SUCCESS,
  LOAD_CARD_RECENT_ACTIVITY_FAILURE,
  LOAD_CARD_RECENT_ACTIVITY_REQUEST,
  LOAD_CARD_RECENT_ACTIVITY_SUCCESS,
  LOAD_CARD_SAMPLE_TYPE_STATUSES_FAILURE,
  LOAD_CARD_SAMPLE_TYPE_STATUSES_REQUEST,
  LOAD_CARD_SAMPLE_TYPE_STATUSES_SUCCESS,
  LOAD_CARD_SAVED_ITEMS_DATA_FAILURE,
  LOAD_CARD_SAVED_ITEMS_DATA_REQUEST,
  LOAD_CARD_SAVED_ITEMS_DATA_SUCCESS,
  LOAD_CARD_SAVED_ITEMS_LISTING_FAILURE,
  LOAD_CARD_SAVED_ITEMS_LISTING_REQUEST,
  LOAD_CARD_SAVED_ITEMS_LISTING_SUCCESS,
  LOAD_DASHBOARDS_FAILURE,
  LOAD_DASHBOARDS_REQUEST,
  LOAD_DASHBOARDS_SUCCESS,
  SAVE_DASHBOARD_FAILURE,
  SAVE_DASHBOARD_REQUEST,
  SAVE_DASHBOARD_SUCCESS,
  SET_DASHBOARD_EDITING_MODE,
  SET_DASHBOARD_FULLSCREEN_MODE,
} from "../action-types.js";
import type { IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";
import { getProjectLabel } from "./config.js";

export interface IExtendedSavedItemData {
  isLoadingItems?: boolean;
  items?: SavedItem[];
  selectionModel?: ISelectionModel;
  meta?: SavedItemsListing;
  activeFilterTerm?: string;
}

export enum DashboardCardType {
  projectsList = "projectsList",
  quickLinks = "quickLinks",
  savedItems = "savedItems",
  gettingStarted = "gettingStarted",
  sampleStatuses = "sampleStatuses",
  recentActivities = "recentActivities",
}

export const availableDashboardCards: IGenericDashboardCard[] = [
  {
    type: DashboardCardType.projectsList,
    name: getProjectLabel(true, true),
    description: "Show the list of projects you are involved in.",
    defaultLayout: { i: "projects-card", w: 1, h: 4 },
    specificConfig: { sorting: SORT_OPTIONS.DATE_MODIFIED.field },
    icon: CardIcons.LISTING,
  },
  {
    type: DashboardCardType.quickLinks,
    name: "Quick Links",
    description: "List of available playbooks, visualizers and launchers.",
    defaultLayout: { i: "quicklink-card", w: 1, h: 4 },
    icon: CardIcons.LISTING,
  },
  {
    type: DashboardCardType.savedItems,
    name: "Saved Items",
    description: "All saved resources you can access.",
    defaultLayout: { i: "saved-items", w: 2, h: 6, minW: 2, minH: 4 },
    specificConfig: { limit: 30 },
    icon: CardIcons.ITEMS,
  },
  {
    type: DashboardCardType.gettingStarted,
    name: "Getting Started",
    description: "Some help for beginners...",
    defaultLayout: { i: "getting-start-card", w: 1, h: 3 },
    icon: CardIcons.TIPS,
  },
  {
    type: DashboardCardType.sampleStatuses,
    name: "Sample Status Summary",
    description: "Summary of sample type-specific status info",
    defaultLayout: { i: "sample-status-card", w: 2, h: 4, minH: 2 },
    specificConfig: { sampleTypeId: null, statusIds: [] },
    licenseModule: LICENSE_MODULE.TRACKER,
    icon: CardIcons.SAMPLES,
  },
  {
    type: DashboardCardType.recentActivities,
    name: "Recent Activities",
    description: "The last few things you looked at",
    defaultLayout: { i: "recent-activity-card", w: 1, h: 2 },
    icon: CardIcons.LISTING,
  },
];

/**
 * Dashboard State definition
 */
export interface IDashboardState {
  dashboards: IDashboardConfig[];
  activeDashboardId: number;
  dashboardEditingMode: boolean;
  dashboardFullscreenMode: boolean;

  projectsListCards: Dictionary<IDashboardCardProjectsList>;
  sampleTypeStatusesCards: Dictionary<IDashboardCardSampleTypeStatusList>;
  recentActivitiesCards: Dictionary<IDashboardCardRecentActivitiesList>;
  savedItemsCards: Dictionary<IDashboardCardSavedItemsList>;
}

const initialState: IDashboardState = {
  dashboards: [],
  activeDashboardId: null,
  dashboardEditingMode: false,
  dashboardFullscreenMode: false,
  projectsListCards: {},
  sampleTypeStatusesCards: {},
  recentActivitiesCards: {},
  savedItemsCards: {},
};

/**
 * Card State definition
 */

interface IDashboardCardData {
  isLoading: boolean;
  misconfigured?: boolean;
}

export interface IDashboardCardProjectsList extends IDashboardCardData, ProjectList {}

export interface IDashboardCardSampleTypeStatusList extends IDashboardCardData, SampleCount {}

export interface IDashboardCardRecentActivitiesList extends IDashboardCardData {
  recentActivities: RecentItem[];
}

export interface IDashboardCardSavedItemsList extends IDashboardCardData {
  listing: IExtendedSavedItemData[];
}

/**
 * Card Configuration definition
 */

export type TSpecificDashboardCardConfig =
  | ISampleStatusesCardConfig
  | IProjectsListCardConfig
  | ISavedItemsCardConfig
  | IRecentActivitiesCardConfig;
// | IXyzCardConfig..., extend as required

export interface IGenericCardConfiguration {
  id: string;
  type: DashboardCardType;
  title: string;
  specificConfig?: TSpecificDashboardCardConfig;
}

/**
 * Reducers
 */
export default createReducer(initialState, {
  // CONSIDER MAKING THIS GENERIC ?
  [LOAD_CARD_PROJECTS_REQUEST]: (state: IDashboardState, payload: { cardId: string }): IDashboardState => {
    return {
      ...state,
      projectsListCards: {
        ...state.projectsListCards,
        [payload.cardId]: { ...state.projectsListCards[payload.cardId], isLoading: true },
      },
    };
  },
  [LOAD_CARD_PROJECTS_SUCCESS]: (state: IDashboardState, payload: any): IDashboardState => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { cardId, ...otherProps } = payload;
    return {
      ...state,
      projectsListCards: {
        ...state.projectsListCards,
        [payload.cardId]: { isLoading: false, ...otherProps },
      },
    };
  },
  [LOAD_CARD_PROJECTS_FAILURE]: (state: IDashboardState, payload: { cardId: string }): IDashboardState => {
    return {
      ...state,
      projectsListCards: {
        ...state.projectsListCards,
        [payload.cardId]: { ...state.projectsListCards[payload.cardId], isLoading: false },
      },
    };
  },
  [LOAD_CARD_SAMPLE_TYPE_STATUSES_REQUEST]: (state: IDashboardState, payload: { cardId: string }): IDashboardState => {
    return {
      ...state,
      sampleTypeStatusesCards: {
        ...state.sampleTypeStatusesCards,
        [payload.cardId]: { ...state.sampleTypeStatusesCards[payload.cardId], isLoading: true },
      },
    };
  },
  [LOAD_CARD_SAMPLE_TYPE_STATUSES_SUCCESS]: (state: IDashboardState, payload: any): IDashboardState => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { cardId, ...otherProps } = payload;
    return {
      ...state,
      sampleTypeStatusesCards: {
        ...state.sampleTypeStatusesCards,
        [payload.cardId]: { isLoading: false, ...otherProps },
      },
    };
  },
  [LOAD_CARD_SAMPLE_TYPE_STATUSES_FAILURE]: (state: IDashboardState, payload: { cardId: string }): IDashboardState => {
    return {
      ...state,
      sampleTypeStatusesCards: {
        ...state.sampleTypeStatusesCards,
        [payload.cardId]: { ...state.sampleTypeStatusesCards[payload.cardId], isLoading: false },
      },
    };
  },
  [SAVE_DASHBOARD_SUCCESS]: (state: IDashboardState, updatedDashboard: IDashboardConfig): IDashboardState => {
    return {
      ...state,
      dashboards: state.dashboards.map((dashboard: IDashboardConfig) =>
        dashboard.id === updatedDashboard.id ? updatedDashboard : dashboard,
      ),
    };
  },
  [LOAD_CARD_RECENT_ACTIVITY_REQUEST]: (state: IDashboardState, payload: { cardId: string }): IDashboardState => {
    return {
      ...state,
      recentActivitiesCards: {
        ...state.recentActivitiesCards,
        [payload.cardId]: { ...state.recentActivitiesCards[payload.cardId], isLoading: true },
      },
    };
  },
  [LOAD_CARD_RECENT_ACTIVITY_SUCCESS]: (state: IDashboardState, payload: any): IDashboardState => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { cardId, ...otherProps } = payload;
    return {
      ...state,
      recentActivitiesCards: {
        ...state.recentActivitiesCards,
        [payload.cardId]: { isLoading: false, ...otherProps },
      },
    };
  },
  [LOAD_CARD_RECENT_ACTIVITY_FAILURE]: (state: IDashboardState, payload: { cardId: string }): IDashboardState => {
    return {
      ...state,
      recentActivitiesCards: {
        ...state.recentActivitiesCards,
        [payload.cardId]: { ...state.recentActivitiesCards[payload.cardId], isLoading: false },
      },
    };
  },
  [LOAD_CARD_SAVED_ITEMS_LISTING_REQUEST]: (state: IDashboardState, payload: { cardId: string }): IDashboardState => {
    return {
      ...state,
      savedItemsCards: {
        ...state.savedItemsCards,
        [payload.cardId]: { ...state.savedItemsCards[payload.cardId], isLoading: true },
      },
    };
  },
  [LOAD_CARD_SAVED_ITEMS_LISTING_SUCCESS]: (
    state: IDashboardState,
    payload: { cardId: string; savedItemsListings: SavedItemsListing[] },
  ): IDashboardState => {
    const updatedListing: IExtendedSavedItemData[] = updateSavedItemsListing(
      state.savedItemsCards[payload.cardId].listing,
      payload.savedItemsListings,
    );
    return {
      ...state,
      savedItemsCards: {
        ...state.savedItemsCards,
        [payload.cardId]: { isLoading: false, listing: updatedListing },
      },
    };
  },
  [LOAD_CARD_SAVED_ITEMS_LISTING_FAILURE]: (state: IDashboardState, payload: { cardId: string }): IDashboardState => {
    return {
      ...state,
      savedItemsCards: {
        ...state.savedItemsCards,
        [payload.cardId]: { ...state.savedItemsCards[payload.cardId], isLoading: false },
      },
    };
  },
  [LOAD_CARD_SAVED_ITEMS_DATA_REQUEST]: (
    state: IDashboardState,
    payload: { cardId: string; listing: SavedItemsItemsArgs },
  ): IDashboardState => {
    const newSavedItemsData: IExtendedSavedItemData[] = updateSavedItemsData(
      state.savedItemsCards[payload.cardId].listing,
      { isLoadingItems: true },
      (savedItemsData) => matchSavedItemsType(savedItemsData.meta, payload.listing),
    );
    return {
      ...state,
      savedItemsCards: {
        ...state.savedItemsCards,
        [payload.cardId]: { ...state.savedItemsCards[payload.cardId], listing: newSavedItemsData },
      },
    };
  },
  [LOAD_CARD_SAVED_ITEMS_DATA_SUCCESS]: (
    state: IDashboardState,
    payload: {
      cardId: string;
      listing: SavedItemsItemsArgs;
      savedItems: SavedItem[];
      filterTerm: string;
    },
  ): IDashboardState => {
    const newSavedItemsData: IExtendedSavedItemData[] = updateSavedItemsData(
      state.savedItemsCards[payload.cardId].listing,
      { isLoadingItems: false, items: payload.savedItems, activeFilterTerm: payload.filterTerm },
      (savedItemsData) => matchSavedItemsType(savedItemsData.meta, payload.listing),
    );
    return {
      ...state,
      savedItemsCards: {
        ...state.savedItemsCards,
        [payload.cardId]: { ...state.savedItemsCards[payload.cardId], listing: newSavedItemsData },
      },
    };
  },
  [LOAD_CARD_SAVED_ITEMS_DATA_FAILURE]: (
    state: IDashboardState,
    payload: { cardId: string; listing: SavedItemsItemsArgs },
  ): IDashboardState => {
    const newSavedItemsData: IExtendedSavedItemData[] = updateSavedItemsData(
      state.savedItemsCards[payload.cardId].listing,
      { isLoadingItems: false },
      (savedItemsData) => matchSavedItemsType(savedItemsData.meta, payload.listing),
    );
    return {
      ...state,
      savedItemsCards: {
        ...state.savedItemsCards,
        [payload.cardId]: { ...state.savedItemsCards[payload.cardId], listing: newSavedItemsData },
      },
    };
  },
  [LOAD_DASHBOARDS_SUCCESS]: (state: IDashboardState, dashboards: IDashboardConfig[]): IDashboardState => {
    return {
      ...state,
      dashboards: dashboards,
      activeDashboardId: dashboards.length > 0 ? dashboards[0].id : null,
    };
  },
  [SET_DASHBOARD_EDITING_MODE]: (state: IDashboardState, editMode: boolean): IDashboardState => {
    return {
      ...state,
      dashboardEditingMode: editMode,
    };
  },
  [SET_DASHBOARD_FULLSCREEN_MODE]: (state: IDashboardState, fullscreenMode: boolean): IDashboardState => {
    return {
      ...state,
      dashboardFullscreenMode: fullscreenMode,
    };
  },
});

/**
 * Action creators
 */
export function fetchDashboards() {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<IDashboardConfig[]> => {
    dispatch({
      type: LOAD_DASHBOARDS_REQUEST,
    });

    return webApiClient
      .fetchDashboardList()
      .then((dashboards: Dashboard[]) => {
        const dashboardConfigs: IDashboardConfig[] = dashboards.map((d: Dashboard): IDashboardConfig => {
          const config: { cardsLayout: any; cardsConfiguration: any } = JSON.parse(d.content);
          return {
            id: d.id,
            title: d.name,
            sortOrder: d.sortOrder,
            dashboardPermissions: null,
            cardsLayout: config.cardsLayout,
            cardsConfiguration: config.cardsConfiguration,
          };
        });

        dispatch({
          type: LOAD_DASHBOARDS_SUCCESS,
          payload: dashboardConfigs,
        });
        return dashboardConfigs;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_DASHBOARDS_FAILURE,
          alertTitle: "Fetching of Dashboards Failed",
        }),
      );
  };
}

export function saveDashboard(dashboard: IDashboardConfig) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<IDashboardConfig[]> => {
    dispatch({
      type: SAVE_DASHBOARD_REQUEST,
    });

    const dashboardInput: InputDashboardInput = {
      id: dashboard.id,
      name: dashboard.title,
      sortOrder: dashboard.sortOrder,
      content: JSON.stringify({ cardsLayout: dashboard.cardsLayout, cardsConfiguration: dashboard.cardsConfiguration }),
    };

    return (
      webApiClient
        .saveDashboard(dashboardInput)
        // Endpoint just returns a boolean!
        // Therefore, we rely on the passed in dashboard config.
        .then(() => {
          dispatch({
            type: SAVE_DASHBOARD_SUCCESS,
            payload: dashboard,
          });

          notify.info(
            {
              title: selMessage({ id: "dashboard.update.successTitle" }),
            },
            { autoClose: 2000 },
          );
          return dashboard;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: SAVE_DASHBOARD_FAILURE,
            alertTitle: selMessage({
              id: "dashboard.update.failureTitle",
            }),
          }),
        )
    );
  };
}

export function loadDashboardCardProjectsList(cardId: string, config: IProjectsListCardConfig) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<Project[]> => {
    dispatch({ type: LOAD_CARD_PROJECTS_REQUEST, payload: { cardId: cardId } });

    return webApiClient
      .fetchDashboardCardProjectsList(config)
      .then((projects: Project[]) => {
        dispatch({
          type: LOAD_CARD_PROJECTS_SUCCESS,
          payload: { cardId: cardId, projects: projects },
        });

        return projects;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_CARD_PROJECTS_FAILURE,
          actionPayload: { cardId: cardId },
          alertTitle: `Fetching of Projects Failed for Card ${cardId}`,
        }),
      );
  };
}

export function loadDashboardCardSampleTypeStatuses(cardId: string, config: ISampleStatusesCardConfig) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient) => {
    dispatch({ type: LOAD_CARD_SAMPLE_TYPE_STATUSES_REQUEST, payload: { cardId: cardId } });

    return webApiClient
      .fetchSampleTypeStatuses(config)
      .then((sampleCounts: SampleCount) => {
        dispatch({
          type: LOAD_CARD_SAMPLE_TYPE_STATUSES_SUCCESS,
          payload: { cardId: cardId, ...sampleCounts },
        });

        return sampleCounts;
      })
      .catch((e) => {
        const notFound: boolean = e.graphQLErrors?.find((graphQLError) => graphQLError.type === NOT_FOUND_EXCEPTION);
        if (notFound) {
          dispatch({
            type: LOAD_CARD_SAMPLE_TYPE_STATUSES_SUCCESS,
            payload: { cardId: cardId, misconfigured: true },
          });
        } else {
          createErrorHandler(dispatch, {
            actionType: LOAD_CARD_SAMPLE_TYPE_STATUSES_FAILURE,
            actionPayload: { cardId: cardId },
            alertTitle: `Fetching Sample Type Statuses Failed for Card ${cardId}`,
          })(e);
        }
      });
  };
}

export function loadDashboardCardRecentActivities(cardId: string, config: IRecentActivitiesCardConfig) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<RecentItem[]> => {
    dispatch({ type: LOAD_CARD_RECENT_ACTIVITY_REQUEST, payload: { cardId: cardId } });

    return webApiClient
      .fetchDashboardCardRecentActivityList(config)
      .then((recentActivities: RecentItem[]) => {
        dispatch({
          type: LOAD_CARD_RECENT_ACTIVITY_SUCCESS,
          payload: { cardId: cardId, recentActivities: recentActivities },
        });

        return recentActivities;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_CARD_RECENT_ACTIVITY_FAILURE,
          actionPayload: { cardId: cardId },
          alertTitle: `Fetching of Recent Activity Failed for Card ${cardId}`,
        }),
      );
  };
}

export function loadDashboardCardSavedItemsListing(cardId: string, _config: ISavedItemsCardConfig) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<SavedItemsListing[]> => {
    dispatch({ type: LOAD_CARD_SAVED_ITEMS_LISTING_REQUEST, payload: { cardId: cardId } });

    return webApiClient
      .fetchSavedItemsListing(-1)
      .then((savedItemsListings: SavedItemsListing[]) => {
        dispatch({
          type: LOAD_CARD_SAVED_ITEMS_LISTING_SUCCESS,
          payload: { cardId: cardId, savedItemsListings: savedItemsListings },
        });
        return savedItemsListings;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_CARD_SAVED_ITEMS_LISTING_FAILURE,
          actionPayload: { cardId: cardId },
          alertTitle: `Fetching of Saved Items Listing Failed for Card ${cardId}`,
        }),
      );
  };
}

export function loadDashboardCardSavedItemsData(
  cardId: string,
  config: ISavedItemsCardConfig,
  activeSavedItemsListing: SavedItemsItemsArgs,
  filterTerm: string,
) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<SavedItem[]> => {
    if (!activeSavedItemsListing) {
      return Promise.resolve([]);
    }
    dispatch({
      type: LOAD_CARD_SAVED_ITEMS_DATA_REQUEST,
      payload: { cardId: cardId, listing: activeSavedItemsListing },
    });

    return webApiClient
      .fetchDashboardCardSavedItems(config, activeSavedItemsListing, filterTerm)
      .then((savedItems: SavedItem[]) => {
        dispatch({
          type: LOAD_CARD_SAVED_ITEMS_DATA_SUCCESS,
          payload: { cardId: cardId, savedItems: savedItems, listing: activeSavedItemsListing, filterTerm: filterTerm },
        });
        return savedItems;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_CARD_SAVED_ITEMS_DATA_FAILURE,
          actionPayload: { cardId: cardId, listing: activeSavedItemsListing },
          alertTitle: `Fetching of Saved Items Data Failed for Card ${cardId}`,
        }),
      );
  };
}

export function setDashboardEditingMode(editMode: boolean) {
  return {
    type: SET_DASHBOARD_EDITING_MODE,
    payload: editMode,
  };
}

export function setDashboardFullscreenMode(fullscreenMode: boolean) {
  return {
    type: SET_DASHBOARD_FULLSCREEN_MODE,
    payload: fullscreenMode,
  };
}

/**
 Utils
 */

export function matchSavedItemsType(a: SavedItemsItemsArgs, b: SavedItemsItemsArgs) {
  return !a || !b ? false : a.type === b.type && a.subType === b.subType;
}

export function updateSavedItemsListing(
  listingToUpdate: IExtendedSavedItemData[],
  savedItemsListings: SavedItemsListing[],
) {
  let newListing: IExtendedSavedItemData[] = listingToUpdate ? listingToUpdate.slice(0) : []; // make sure the redux state is not mutated
  savedItemsListings?.forEach((savedItemsListing: SavedItemsListing) => {
    const existingListing: IExtendedSavedItemData = newListing?.find((savedItemsData) =>
      savedItemsListingExists(savedItemsData, savedItemsListing),
    );
    // if listing already exists, we only wanna update the meta information
    if (existingListing) {
      newListing = updateSavedItemsListingData(newListing, savedItemsListing, (savedItemsData) =>
        savedItemsListingExists(savedItemsData, savedItemsListing),
      );
    } else {
      // initialization of the listing using the IExtendedSavedItemData map
      newListing.push({
        meta: savedItemsListing,
        selectionModel: emptySelectionModel,
        isLoadingItems: false,
        items: [],
      });
    }
  });
  return newListing;
}

export const getSavedItemsLists = (savedItemsData: IExtendedSavedItemData[]): IExtendedSavedItemData[] => {
  if (!savedItemsData) {
    return [];
  }
  return savedItemsData.filter((savedItemsList: IExtendedSavedItemData) => {
    return savedItemsList.meta.count > 0; // filter out empty categories
  });
};
